import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { marketplaceTitle } from "@/api/returns";
import * as sync from "@/api/stock_sync";
import type { StockSyncPreview, StockSyncPreviewRow } from "@/api/stock_sync";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { LoadState, useGoBack } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import { STOCK_SYNC_WAREHOUSES_KEY } from "@/pages/settings/cards/StockSyncWarehousesCard";
import {
  FILTERS,
  type PreviewFilter,
  dateTime,
  demandSum,
  explainLines,
  fmt,
  isChecked,
  isDiverged,
  isHeld,
  isSelectable,
  keptHint,
  lockedHint,
  matchesFilter,
  matchesSearch,
  sameSet,
  skipTitle,
} from "@/pages/settings/stock-sync-preview";

const BACK = "/settings/fbs/stock";
const COLUMNS = 13;

/**
 * Склад маркетплейса в синке: что отмечено к публикации и сколько опубликуется.
 *
 * Расчёт — живой (`GET …/preview`: 1С, пул, чтение площадки) и без записи,
 * поэтому сам не перечитывается: только по «Обновить расчёт». Галки — черновик
 * до «Сохранить выбор», уходит ПОЛНЫЙ список (`PUT …/selection` — замена).
 */
export function StockSyncWarehousePage(): React.ReactElement {
  const { marketplace = "", warehouseKey = "" } = useParams();
  const goBack = useGoBack(BACK);
  const q = useQuery({
    queryKey: ["stock-sync-preview", marketplace, warehouseKey],
    queryFn: () => sync.warehousePreview(marketplace, warehouseKey),
    // Каждый запрос — 1С и площадка: не повторять молча и не перечитывать по фокусу.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={goBack} aria-label="Назад">
          <ArrowLeft className="size-4" />
        </Button>
        <h1 className="text-xl font-bold">
          Остатки: склад {q.data?.warehouse_name ?? warehouseKey} ·{" "}
          {marketplaceTitle(marketplace)}
        </h1>
      </div>
      {q.isLoading || q.error || !q.data ? (
        <LoadState
          isLoading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          className="flex flex-col items-center gap-3 p-4 text-center"
        />
      ) : (
        <WarehouseBody
          preview={q.data}
          refreshing={q.isFetching}
          onRefresh={() => q.refetch()}
        />
      )}
    </div>
  );
}

function WarehouseBody({
  preview,
  refreshing,
  onRefresh,
}: {
  preview: StockSyncPreview;
  refreshing: boolean;
  onRefresh: () => Promise<unknown>;
}): React.ReactElement {
  const qc = useQueryClient();
  const mp = preview.marketplace;
  const wh = preview.warehouse_key;

  // Все отмеченные в базе — и те, что сейчас неоднозначны/пропали: бэкенд принимает
  // их в полном списке, отметка сохраняется, пока её не снимут галкой.
  const saved = useMemo(
    () => new Set(preview.rows.filter((r) => r.selected).map((r) => r.unit_key)),
    [preview.rows],
  );
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const selected = draft ?? saved;
  const dirty = !sameSet(selected, saved);

  const [actionError, setActionError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PreviewFilter>("all");
  const [search, setSearch] = useState("");
  const needle = useDeferredValue(search.trim().toLowerCase());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const afterWrite = async (row: sync.StockSyncWarehouse, what: string) => {
    toast.success(`${what}: отмечено ${row.selected_count}`);
    setActionError(null);
    void qc.invalidateQueries({ queryKey: STOCK_SYNC_WAREHOUSES_KEY });
    // Черновик держим, пока не приедет расчёт с сохранённым выбором, —
    // иначе на миг показались бы старые галки.
    await onRefresh();
    setDraft(null);
  };
  const fail = (err: unknown) => setActionError(extractErrorMessage(err, "Не удалось сохранить"));

  const save = useMutation({
    mutationFn: (units: string[]) => sync.saveSelection(mp, wh, units),
    onMutate: () => setActionError(null),
    onSuccess: (row) => afterWrite(row, "Выбор сохранён"),
    onError: fail,
  });
  const allMapped = useMutation({
    mutationFn: () => sync.selectAllMapped(mp, wh),
    onMutate: () => setActionError(null),
    onSuccess: (row) => afterWrite(row, "Отмечены все сопоставленные"),
    onError: fail,
  });

  const toggle = useCallback(
    (unit: string) =>
      setDraft((prev) => {
        const next = new Set(prev ?? saved);
        if (next.has(unit)) next.delete(unit);
        else next.add(unit);
        return next;
      }),
    [saved],
  );
  const toggleExpand = useCallback(
    (unit: string) =>
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(unit)) next.delete(unit);
        else next.add(unit);
        return next;
      }),
    [],
  );

  const counts = useMemo(() => {
    const c = {} as Record<PreviewFilter, number>;
    for (const f of FILTERS) {
      c[f.key] = preview.rows.filter((r) => matchesFilter(r, f.key, selected, preview)).length;
    }
    return c;
  }, [preview, selected]);
  const visible = useMemo(
    () =>
      preview.rows.filter(
        (r) => matchesFilter(r, filter, selected, preview) && matchesSearch(r, needle),
      ),
    [preview, filter, selected, needle],
  );

  const busy = save.isPending || allMapped.isPending;

  return (
    <div className="space-y-4">
      <div className="grid gap-x-6 gap-y-1 rounded-md border p-3 text-sm sm:grid-cols-2">
        <div>
          Кабинет: <b>{preview.cabinet_name}</b>
        </div>
        <div>
          Склад маркетплейса: <b>{preview.warehouse_name}</b>{" "}
          <span className="text-muted-foreground">({preview.warehouse_key})</span>
        </div>
        <div>
          Склад 1С: <b>{preview.one_c_warehouse_name ?? "—"}</b>
        </div>
        <div>
          Синхронизация склада:{" "}
          <b className={preview.enabled ? "" : "text-muted-foreground"}>
            {preview.enabled ? "включена" : "выключена"}
          </b>
          {" · "}публикация на {marketplaceTitle(preview.marketplace)}:{" "}
          {preview.platform_enabled ? "включена" : "выключена"}
        </div>
        <div>
          Режим:{" "}
          <b className={preview.dry_run ? "" : "text-amber-700"}>
            {preview.dry_run
              ? "сухой прогон — на маркетплейс не отправляется"
              : "рабочий — остатки отправляются на маркетплейс"}
          </b>
          {" · "}макс. изменение за прогон {preview.max_delta} шт.
        </div>
        <div>
          Расчёт на: <b>{dateTime(preview.computed_at)}</b>
          <span className="text-muted-foreground">
            {" "}
            · заказы — загруженные в портал
            {preview.orders_polled_at
              ? ` (загрузка ${dateTime(preview.orders_polled_at)})`
              : " (время загрузки не хранится)"}
          </span>
        </div>
        <div className="sm:col-span-2">
          Последний прогон: {dateTime(preview.last_run_at)}
          {preview.last_run_error && (
            <span className="text-destructive"> · {preview.last_run_error}</span>
          )}
        </div>
      </div>

      {preview.notes.length > 0 && (
        <ul className="list-disc pl-5 text-xs text-muted-foreground">
          {preview.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => void onRefresh()} disabled={refreshing}>
          {refreshing ? (
            <Loader2 className="mr-1 size-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 size-4" />
          )}
          Обновить расчёт
        </Button>
        <Button size="sm" variant="outline" onClick={() => allMapped.mutate()} disabled={busy}>
          Отметить все сопоставленные
        </Button>
        <Button
          size="sm"
          onClick={() => save.mutate([...selected].sort())}
          disabled={!dirty || busy}
        >
          {save.isPending && <Loader2 className="mr-1 size-4 animate-spin" />}
          Сохранить выбор
        </Button>
        {dirty && <span className="text-xs text-amber-700">выбор изменён, не сохранён</span>}
      </div>

      {actionError && <Banner>{actionError}</Banner>}
      {preview.platform_error && (
        <Banner>
          Не удалось получить остатки с маркетплейса — колонка «На маркетплейсе» не заполнена,
          расчёт показан: {preview.platform_error}
        </Banner>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-64"
          placeholder="Артикул или название"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={filter === f.key ? "default" : "outline"}
            onClick={() => setFilter(f.key)}
          >
            {f.title} ({counts[f.key]})
          </Button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr className="align-bottom">
              <th className="py-1 pr-2" />
              <th className="pr-2">Артикул</th>
              <th className="pr-2">Товар 1С</th>
              <th className="pr-2">Статус</th>
              <th className="pr-2 text-right">Остаток владельца</th>
              <th className="pr-2 text-right">Остаток продавца</th>
              <th className="pr-2 text-right">Собрано, не проведено</th>
              <th className="pr-2 text-right">Заказы Wildberries</th>
              <th className="pr-2 text-right">Заказы Ozon</th>
              <th className="pr-2 text-right">Общий остаток</th>
              <th className="pr-2 text-right">К публикации</th>
              <th className="pr-2 text-right">На маркетплейсе</th>
              <th className="text-right">Опубликовано ранее</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <PreviewRowView
                key={r.unit_key}
                row={r}
                checked={isChecked(r, selected)}
                expanded={expanded.has(r.unit_key)}
                onToggle={toggle}
                onExpand={toggleExpand}
              />
            ))}
          </tbody>
        </table>
        {visible.length === 0 && (
          <p className="p-3 text-sm text-muted-foreground">Нет товаров по заданному фильтру.</p>
        )}
      </div>
    </div>
  );
}

function Banner({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
    >
      <AlertTriangle className="mr-1 inline size-4" />
      {children}
    </div>
  );
}

function Num({ v }: { v: number | null | undefined }): React.ReactElement {
  return <>{v === null || v === undefined ? "—" : fmt(v)}</>;
}

/** Строка таблицы. `memo`: на 300 строках галка перерисовывает только себя. */
const PreviewRowView = React.memo(function PreviewRowView({
  row,
  checked,
  expanded,
  onToggle,
  onExpand,
}: {
  row: StockSyncPreviewRow;
  checked: boolean;
  expanded: boolean;
  onToggle: (unit: string) => void;
  onExpand: (unit: string) => void;
}): React.ReactElement {
  const b = row.breakdown;
  const d = b ? demandSum(b) : null;
  const hint = lockedHint(row);
  const kept = keptHint(row);
  const label = row.article || row.unit_key;
  return (
    <>
      <tr className="border-t align-top" data-testid={`row-${row.unit_key}`}>
        <td className="py-1 pr-2">
          <input
            type="checkbox"
            aria-label={`Отметить ${label}`}
            title={hint ?? kept ?? undefined}
            checked={checked}
            disabled={!isSelectable(row)}
            onChange={() => onToggle(row.unit_key)}
          />
        </td>
        <td className="pr-2">
          <button
            type="button"
            className="flex items-start gap-1 text-left"
            aria-label={`Расшифровка ${label}`}
            aria-expanded={expanded}
            onClick={() => onExpand(row.unit_key)}
          >
            {expanded ? (
              <ChevronDown className="mt-0.5 size-3 shrink-0" />
            ) : (
              <ChevronRight className="mt-0.5 size-3 shrink-0" />
            )}
            <span>
              {label}
              <span className="block text-xs text-muted-foreground">{row.unit_key}</span>
            </span>
          </button>
        </td>
        <td className="pr-2">
          {row.name_1c ?? <span className="text-muted-foreground">не сопоставлен</span>}
        </td>
        <td className="pr-2 text-xs">
          {row.status_text}
          {hint && row.status === "zero_unidentified" && (
            <span className="block text-muted-foreground">{hint}</span>
          )}
          {kept && <span className="block text-amber-800">{kept}</span>}
        </td>
        <td className="pr-2 text-right tabular-nums"><Num v={b?.owner_stock} /></td>
        <td className="pr-2 text-right tabular-nums"><Num v={b?.seller_stock} /></td>
        <td className="pr-2 text-right tabular-nums"><Num v={d?.in_flight} /></td>
        <td className="pr-2 text-right tabular-nums"><Num v={d?.wb} /></td>
        <td className="pr-2 text-right tabular-nums"><Num v={d?.ozon} /></td>
        <td className="pr-2 text-right tabular-nums"><Num v={b?.pool_rest} /></td>
        <td className="pr-2 text-right tabular-nums">
          <b>{row.will_publish ?? "—"}</b>
          {row.status === "zero_unselected" && b && (
            <span className="block text-xs text-muted-foreground">расчётный: {b.result}</span>
          )}
          {isHeld(row) ? (
            <span className="block text-xs text-amber-700" title={row.max_delta_hold ?? undefined}>
              изменение больше допустимого: {row.will_publish} будет опубликовано после подтверждения
            </span>
          ) : (
            row.max_delta_hold && (
              <span className="block text-xs text-muted-foreground">{row.max_delta_hold}</span>
            )
          )}
          {row.decision_skip && !isHeld(row) && (
            <span className="block text-xs text-amber-700">{skipTitle(row.decision_skip)}</span>
          )}
        </td>
        <td
          className={cn(
            "pr-2 text-right tabular-nums",
            isDiverged(row) && "bg-amber-100 font-medium text-amber-900",
          )}
          title={isDiverged(row) ? "на маркетплейсе другое значение" : undefined}
        >
          <Num v={row.platform_qty} />
        </td>
        <td className="text-right tabular-nums">
          <Num v={row.last_published_qty} />
          {row.last_published_at && (
            <span className="block text-xs text-muted-foreground">
              {dateTime(row.last_published_at)}
            </span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td />
          <td colSpan={COLUMNS - 1} className="pb-2 text-xs">
            <div className="space-y-0.5 rounded-md bg-muted/50 p-2">
              {b ? (
                explainLines(b).map((line) => <div key={line}>{line}</div>)
              ) : (
                <div>{row.status_text} — расчёта нет.</div>
              )}
              {row.max_delta_hold && <div>{row.max_delta_hold}</div>}
              {row.last_computed_qty !== null && (
                <div>рассчитано последним прогоном: {row.last_computed_qty}</div>
              )}
              {row.skip_reason && (
                <div>не опубликовано последним прогоном: {skipTitle(row.skip_reason)}</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
});
